import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import apiService from '../../services/api';
import Button from '../UI/Button';
import Input from '../UI/Input';
import Modal from '../UI/Modal';

export interface CreateSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (section: { _id: string; name: string; description?: string; schedules?: any[] }) => void;
}

const CreateSectionModal: React.FC<CreateSectionModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [schedules, setSchedules] = useState<Array<{ _id: string; name: string }>>([]);
  const [formData, setFormData] = useState<{ name: string; description: string; schedules: string[] }>({
    name: '',
    description: '',
    schedules: [],
  });

  useEffect(() => {
    if (isOpen) {
      apiService.getSchedules().then((res) => {
        const list = res.data?.schedules || res.data || [];
        setSchedules(Array.isArray(list) ? list : []);
      }).catch(() => setSchedules([]));
    }
  }, [isOpen]);

  const handleClose = () => {
    setFormData({ name: '', description: '', schedules: [] });
    onClose();
  };

  const handleScheduleToggle = (scheduleId: string) => {
    setFormData((prev) => {
      const next = prev.schedules.includes(scheduleId)
        ? prev.schedules.filter((id) => id !== scheduleId)
        : [...prev.schedules, scheduleId];
      return { ...prev, schedules: next };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Section name is required');
      return;
    }
    try {
      setSubmitting(true);
      const res = await apiService.createSection({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        schedules: formData.schedules.length > 0 ? formData.schedules : undefined,
      });
      const section = res.data?.section || res.data;
      toast.success('Section created successfully');
      onSuccess(section);
      handleClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to create section');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Section" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name *"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Section name"
          required
          fullWidth
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Schedules (optional)</label>
          <div className="border border-gray-300 rounded-md px-3 py-2 bg-white max-h-40 overflow-y-auto space-y-1">
            {schedules.map((schedule) => (
              <label key={schedule._id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  checked={formData.schedules.includes(schedule._id)}
                  onChange={() => handleScheduleToggle(schedule._id)}
                />
                <span>{schedule.name}</span>
              </label>
            ))}
            {schedules.length === 0 && (
              <p className="text-xs text-gray-500">No schedules available.</p>
            )}
          </div>
        </div>
        <Input
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Optional description"
          fullWidth
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            Create Section
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateSectionModal;
